import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import translationService from './services/translationService';

// Basic resources with English as the source language
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: 'Home',
        about: 'About',
        services: 'Services',
        portfolio: 'Portfolio',
        partners: 'Partners',
        news: 'News',
        metalDesigns: 'Metal Designs',
        careers: 'Jobs',
        contact: 'Contact',
        translation: 'Translation Demo'
      },
      // Common elements
      common: {
        readMore: 'Read More',
        learnMore: 'Learn More',
        getStarted: 'Get Started',
        viewAll: 'View All',
        since: 'Since',
        today: 'Today',
        ourJourney: 'Our Journey',
        ourExpertise: 'Our Expertise',
        whyChooseUs: 'Why Choose Us?',
        getInTouch: 'Get In Touch',
        findUs: 'Find Us',
        additionalProjects: 'Additional Projects',
        ourProcess: 'Our Process',
        readyToStart: 'Ready to Start Your Project?',
        joinOurTeam: 'Join Our Team',
        openPositions: 'Open Positions',
        applicationForm: 'Application Form',
        newsletterArchive: 'Newsletter Archive',
        signatureProjects: 'Signature Projects',
        letSCreateSomething: 'Let\'s Create Something Extraordinary',
        startYourProject: 'Start Your Project',
        requestQuote: 'Request Quote',
        getYourQuote: 'Get Your Quote Today',
        viewOurWork: 'View Our Work',
        exploreServices: 'Explore Services',
        contactExperts: 'Contact Our Experts',
        years: 'years',
        projects: 'projects',
        experience: 'experience',
        excellence: 'excellence',
        innovation: 'innovation',
        quality: 'quality',
        craftsmanship: 'craftsmanship',
        consultation: 'consultation',
        installation: 'installation',
        maintenance: 'maintenance',
        support: 'support'
      },
      // Home Page
      home: {
        heroTitle: 'Heart and Soul for Your Theater.',
        heroSubtitle: 'With more than 200 projects, Stapotech BV is an experienced partner in custom production and parts supply. Since 2001, we have been working with our Belgian company, but our origins date back to 1888 – now five generations of family business. We produce everything ourselves, from start to finish, with full focus on quality and precision.',
        explorePortfolio: 'Explore Portfolio',
        contactUs: 'Contact Us',
        quickLinksTitle: 'Quick Access',
        newsLink: 'Latest News',
        metalDesignsLink: 'Metal Designs',
        aboutLink: 'About Us',
        heritage: {
          title: 'Since 2001',
          description: 'Lisanne is the 5th generation after Han. The company has always been passed down from father to son and now from father to daughter.',
          legacy: 'With more than 200 successful projects to our name, we are building a history of quality, craftsmanship and innovation. \n\nIn our own production hall, we make everything fully custom – from the first design to the last finish. \n\nThis way, we guarantee perfection in every detail. \n\nIn addition, we are also a reliable supplier of high-quality parts, for those seeking the same top quality.',
          yearsOfExcellence: 'Years of Excellence'
        },
        expertise: {
          title: 'Our Expertise',
          subtitle: 'From intimate theaters to grand opera houses, we create the invisible magic that brings performances to life.',
          stageRigging: {
            title: 'Theater Technical Lifting Systems',
            description: 'We build custom theater winches from A to Z for your theater. We have solutions for small and large halls.'
          },
          lightingSystems: {
            title: 'Self-Retracting Trusses',
            description: 'Self-climbing trusses using 2 motors in a standard square truss. Ideal for halls with limited foundation possibilities. The trusses are quiet because they are hoisted with steel cables and hang very stable because they use 4 steel cables.'
          },
          movableFloors: {
            title: 'Movable Floors',
            description: 'Revolutionary movable floor systems that transform spaces instantly, creating multi-level stages and orchestra pits with architectural precision.'
          }
        },
        signatureProjects: {
          title: 'Signature Projects',
          musis: {
            title: 'Musis Sacrum, Arnhem',
            year: '2017',
            location: 'Netherlands',
            description: 'A technological masterpiece featuring six precision trommeltrekken, nine independently controlled movable floors, and three orchestra lifts. This installation represents the pinnacle of modern theater engineering.',
            features: {
              trommeltrekken: '6 Trommeltrekken',
              variableSpeed: 'Variable speed control',
              movableFloors: '9 Movable Floors',
              independentOperation: 'Independent operation'
            }
          },
          schalm: {
            title: 'De Schalm, Veldhoven',
            year: '2015',
            location: 'Netherlands',
            description: 'A complete renovation that expanded possibilities with two new side bridges, a wider portal bridge, and four state-of-the-art trommeltrekken systems, transforming a traditional space into a modern marvel.',
            features: {
              renovation: 'Complete Renovation',
              modernTechnology: 'Modern technology integration',
              trommeltrekken: '4 Trommeltrekken',
              enhanced: 'Enhanced capabilities'
            }
          }
        },
        philosophy: {
          quote: 'The show must go on!',
          description: 'Quality and solution-oriented thinking are central to everything we do. This way, we guarantee perfection in every detail. In addition, we are also a reliable supplier of high-quality parts for those seeking the same top quality.',
          author: 'Han Stakebrand',
          title: 'Founder & Technical Director'
        },
        contactPreview: {
          title: 'Let\'s Create Something Extraordinary',
          description: 'Whether you\'re planning a new theater, renovating an existing space, or need custom solutions, we\'re here to bring your vision to life with the same dedication that has driven us for over 20 years.',
          address: 'Rodenrijt 80, 3930 Hamont-Achel, Belgium',
          phone: '+32(0)11-667894',
          email: 'info@stapotech.be'
        },
        cta: {
          title: 'Ready to Transform Your Theater?',
          subtitle: 'Let\'s discuss your project requirements and create something extraordinary together. Contact our team of experts today.'
        }
      },
      // About Page
      about: {
        title: 'About Us',
        subtitle: 'From our founding in 2001 to today\'s innovations, Stapotech BV continues the legacy of excellence in theater technology and custom solutions.',
        history: {
          title1: 'Forge J.H. Stakebrand',
          desc1: 'On February 13, 1888, Forge J.H. Stakebrand opened its doors at Lijnbaansgracht 290 in Amsterdam. In 1923, an expansion followed with two more adjacent buildings and the name was changed to J.H. Stakebrand & Son. At this historic location, no fewer than three generations of the Stakebrand family practiced their craft with skill and dedication.',
          title2: '100th Anniversary',
          desc2: 'In 2002, our company celebrated its 100th anniversary, then under the name Stakebrand B.V. From our location in Heeze and with a Belgian subsidiary, Stakebrand B.V.B.A. in Hamont-Achel, we have built a rich history. Under the leadership of Han Stakebrand, fourth generation, and his father Jan Stakebrand, third generation, the family business grew further. During this period, important technological innovations were implemented: the development from the traditional hand-operated pulling wall to the modern, electrically driven version.',
          title3: 'Stapotech BV',
          desc3: 'Since 2001, we have been working from Belgium under the name Stapotech BV (Stakebrand Stage Techniques). In 2024, the fifth generation, Lisanne Stakebrand, took over the helm. Today, we combine craftsmanship with strong partnerships, so that we can work customer-oriented and efficiently with the quality that has characterized us for more than a century.'
        },
        location: {
          title: 'Our Location',
          headquarters: 'Headquarters',
          production: 'Production Site',
          serviceArea: 'Service Area',
          serviceAreaDesc: 'Belgium & Netherlands - Just 10 minutes from the Dutch border'
        },
        team: {
          title: 'Our Team',
          subtitle: 'Meet the experts who bring decades of experience and passion to every project',
          han: {
            name: 'Han Stakebrand',
            role: 'Founder & Technical Director',
            experience: '30+ years in theater technology'
          },
          lisanne: {
            name: 'Lisanne Stakebrand',
            role: '5th Generation - Operations Director',
            experience: '15+ years in theater technology innovation'
          }
        }
      },
      // Portfolio Page
      portfolio: {
        title: 'Our Portfolio',
        subtitle: 'Discover our exceptional theater installations and technical solutions across Belgium and the Netherlands',
        projects: {
          musis: {
            title: 'Musis Sacrum Arnhem',
            year: '2017',
            description: '6 trommeltrekken, 9 movable floors, 3 orchestra lifts, variable stage'
          },
          schalm: {
            title: 'De Schalm Veldhoven',
            year: '2015',
            description: 'Complete renovation with 4 trommeltrekken and enhanced capabilities'
          },
          castellum: {
            title: 'Theater Castellum',
            year: '2018',
            description: 'New theater construction with advanced stage technology and modern rigging systems'
          },
          vondel: {
            title: 'Vondelpark Theater',
            year: '2019',
            description: 'Regional theater upgrade with enhanced lighting systems and movable stage elements'
          },
          leest: {
            title: 'Cultural Center De Leest',
            year: '2020',
            description: 'Small hall renovation with compact rigging solutions and flexible stage configuration'
          },
          corrosia: {
            title: 'Corrosia Cultural Center',
            year: '2021',
            description: 'Modern cultural center with integrated theater technology and multi-purpose stage systems'
          }
        }
      },
      // News Page
      news: {
        title: 'Latest News',
        subtitle: 'Stay updated with our latest projects, innovations, and industry insights',
        newsletter: {
          title: 'Subscribe to Our Newsletter',
          subtitle: 'Get the latest updates from Stapotech delivered to your inbox',
          emailPlaceholder: 'Enter your email address',
          subscribe: 'Subscribe',
          thankYou: 'Thank You',
          successMessage: 'You have been successfully subscribed to our newsletter.'
        }
      },
      // Metal Designs Page
      metalDesigns: {
        title: 'Metal Designs',
        subtitle: 'Custom laser-cut and powder-coated designs for logos, names, and interior elements',
        services: {
          title: 'Custom Metal Solutions',
          precision: {
            title: 'Laser Cutting Precision',
            description: 'High-precision laser cutting for materials up to 2mm thickness'
          },
          coating: {
            title: 'Powder Coating',
            description: 'Durable powder coating in various colors and finishes'
          },
          custom: {
            title: 'Custom Designs',
            description: 'Logos, names, interior elements, and decorative pieces'
          }
        },
        process: {
          title: 'Our Process',
          subtitle: 'From concept to completion',
          steps: {
            design: {
              title: 'Design',
              description: 'We work with you to create the perfect design for your needs'
            },
            cutting: {
              title: 'Laser Cutting',
              description: 'Precision laser cutting with state-of-the-art equipment'
            },
            coating: {
              title: 'Coating',
              description: 'Professional powder coating in your choice of colors'
            },
            delivery: {
              title: 'Delivery',
              description: 'Quality-controlled and delivered to your specifications'
            }
          }
        },
        requestQuote: 'Request Quote',
        cta: {
          title: 'Ready to Start Your Project?',
          description: 'Contact us today to discuss your custom metal design requirements and get a personalized quote.'
        }
      },
      // Careers Page
      careers: {
        title: 'Jobs',
        subtitle: 'Join our team of theater technology experts',
        openPositions: 'Open Positions',
        loading: 'Loading job vacancies...',
        noVacancies: 'No job vacancies available at the moment.',
        requirements: 'Requirements',
        location: 'Location',
        type: 'Type',
        salary: 'Salary',
        applyNow: 'Apply Now',
        submitting: 'Submitting...',
        whyWorkWithUs: 'Why Work With Us?',
        whyWorkWithUsSubtitle: 'Join a team that values innovation, quality, and craftsmanship',
        benefits: {
          excellence: {
            title: '100+ Years of Excellence',
            description: 'Be part of a company with over a century of expertise and innovation in theater technology.'
          },
          growth: {
            title: 'Professional Growth',
            description: 'Develop your skills in cutting-edge theater technology and custom metal design projects.'
          },
          team: {
            title: 'Team Environment',
            description: 'Work with passionate professionals who care about quality and customer satisfaction.'
          }
        },
        jobs: {
          installer: {
            title: 'Technical Installer',
            description: 'Technical Installer wanted, 40h/week. Experience with theater rigging systems and mechanical installations required.',
            requirements: 'Requirements: Technical education, 3+ years experience, flexible schedule',
            apply: 'Apply Now'
          },
          designer: {
            title: 'Design Specialist',
            description: 'Design Specialist, creative input for custom metal designs and theater installations.',
            requirements: 'Requirements: Design education, CAD experience, creative mindset',
            apply: 'Apply Now'
          }
        },
        applicationForm: {
          title: 'Application Form',
          name: 'Full Name',
          email: 'Email Address',
          phone: 'Phone Number',
          position: 'Position Applied For',
          coverLetter: 'Cover Letter',
          cv: 'Upload CV',
          submit: 'Submit Application',
          submitted: 'Application Submitted',
          successMessage: 'Thank you for your interest. We will review your application and contact you soon.'
        }
      },
      // Contact Page
      contact: {
        title: 'Contact Us',
        subtitle: 'Ready to bring your theater vision to life? Get in touch with our experts',
        getInTouch: 'Get In Touch',
        headquarters: 'Headquarters',
        production: 'Production Site',
        phoneAndFax: 'Phone & Fax',
        email: 'Email',
        sendMessage: 'Send a Message',
        contactInformation: 'Contact Information',
        form: {
          name: 'Name',
          email: 'Email',
          address: 'Address',
          postcode: 'Postcode',
          city: 'City',
          phone: 'Phone',
          message: 'Message',
          submit: 'Send Message',
          thankYou: 'Thank You',
          successMessage: 'Your message has been sent successfully. We will contact you soon.'
        },
        map: {
          title: 'Find Us',
          description: 'Located in Hamont-Achel, Belgium - Just 10 minutes from the Dutch border',
          headquarters: 'Headquarters',
          productionFacility: 'Production Facility'
        }
      },
        // Translation Demo Page
        translationDemo: {
          title: 'Translation System Demo',
          subtitle: 'Test automatic and manual translation features',
          manualSection: 'Manual Translations'
        },
        partners: {
         title: 'Partners',
         description: 'For quite some time, we have been a supplier of high-quality components. Thanks to our extensive and modern machinery, we can respond quickly and flexibly to the wishes of our clients. Our in-depth sector experience enables us to establish strong, long-term collaborative relationships with various partners, where we jointly strive for optimal results.',
       },
        // Footer
        footer: {
        company: 'Stapotech BV',
        description: 'Your partner in stage technology, where every project tells its own story.',
        quickLinks: 'Quick Links',
        services: 'Services',
        contact: 'Contact',
        followUs: 'Follow Us',
        copyright: '© 2025 Stapotech BV. All rights reserved.',
        rights: 'All rights reserved.',
        privacy: 'Privacy Policy',
        terms: 'Terms & Conditions',
        links: {
          home: 'Home',
          about: 'About',
          portfolio: 'Portfolio',
          news: 'News',
          metalDesigns: 'Metal Designs',
          careers: 'Jobs',
          contact: 'Contact'
        },
        servicesList: {
          hijsinstallaties: 'Theater Hoisting Installations',
          vloeren: 'Movable Floors',
          draaitonelen: 'Revolving Stages',
          trussen: 'Self-Climbing Trusses',
          metal: 'Metal Designs',
          theaterTechnology: 'Theater Technology',
          customMetalDesigns: 'Custom Metal Designs',
          installation: 'Installation & Maintenance',
          consultation: 'Consultation & Design'
        },
        companyList: {
          about: 'About',
          portfolio: 'Portfolio',
          news: 'News',
          contact: 'Contact'
        },
        contactInfo: {
          address: 'Address',
          phone: 'Phone',
          email: 'Email',
          hours: 'Business Hours',
          weekdays: 'Mon-Fri: 8:00 AM - 5:00 PM',
          weekend: 'Sat-Sun: Closed'
        }
      }
    }
  },
  nl: {
    translation: {
      // Navigation
      nav: {
        home: 'Home',
        about: 'Over Ons',
        services: 'Diensten',
        portfolio: 'Portfolio',
        partners: 'Partners',
        news: 'Nieuws',
        metalDesigns: 'Metaal Ontwerpen',
        careers: 'Vacatures',
        contact: 'Contact',
        translation: 'Vertaling Demo'
      },
      // Common elements
      common: {
        readMore: 'Lees Meer',
        learnMore: 'Meer Informatie',
        getStarted: 'Begin Nu',
        viewAll: 'Bekijk Alles',
        since: 'Sinds',
        today: 'Vandaag',
        ourJourney: 'Onze Reis',
        ourExpertise: 'Onze Expertise',
        whyChooseUs: 'Waarom Kiezen Voor Ons?',
        getInTouch: 'Neem Contact Op',
        findUs: 'Vind Ons',
        additionalProjects: 'Aanvullende Projecten',
        ourProcess: 'Ons Proces',
        readyToStart: 'Klaar Om Uw Project Te Starten?',
        joinOurTeam: 'Word Lid Van Ons Team',
        openPositions: 'Open Posities',
        applicationForm: 'Sollicitatieformulier',
        newsletterArchive: 'Nieuwsbrief Archief',
        signatureProjects: 'Kenmerkende Projecten',
        letSCreateSomething: 'Laten We Iets Buitengewoons Creëren',
        startYourProject: 'Start Uw Project',
        requestQuote: 'Vraag Offerte Aan',
        getYourQuote: 'Krijg Vandaag Uw Offerte',
        viewOurWork: 'Bekijk Ons Werk',
        exploreServices: 'Ontdek Diensten',
        contactExperts: 'Neem Contact Op Met Onze Experts',
        years: 'jaar',
        projects: 'projecten',
        experience: 'ervaring',
        excellence: 'excellentie',
        innovation: 'innovatie',
        quality: 'kwaliteit',
        craftsmanship: 'vakmanschap',
        consultation: 'consultatie',
        installation: 'installatie',
        maintenance: 'onderhoud',
        support: 'ondersteuning'
      },
      // Home Page
      home: {
        heroTitle: 'Hart en Ziel voor uw theater.',
        heroSubtitle: 'Met meer dan 200 projecten is Stapotech BV een ervaren partner in maatwerkproductie en onderdelenlevering. Sinds 2001 werken wij met onze Belgische firma, maar onze oorsprong gaat terug tot 1888 – inmiddels vijf generaties familiebedrijf. We produceren alles zelf, van begin tot eind, met volledige focus op kwaliteit en precisie.',
        explorePortfolio: 'Ontdek Portfolio',
        contactUs: 'Neem Contact Op',
        quickLinksTitle: 'Snelle Toegang',
        newsLink: 'Laatste Nieuws',
        metalDesignsLink: 'Metaal Ontwerpen',
        aboutLink: 'Over Ons',
        heritage: {
          title: 'Sinds 2001',
          description: 'Lisanne is de 5de generatie na Han. Het bedrijf ging steeds over van vader op zoon en nu dus van vader op dochter.',
          legacy: 'Met meer dan 200 succesvolle projecten op onze naam, bouwen wij aan een geschiedenis van kwaliteit, vakmanschap en innovatie. \n\nIn onze eigen productiehal maken we alles volledig op maat – van het eerste ontwerp tot de laatste afwerking. \n\nZo garanderen we perfectie in elk detail. \n\nDaarnaast zijn wij ook een betrouwbare toeleverancier van hoogwaardige onderdelen, voor wie dezelfde topkwaliteit zoekt.',
          yearsOfExcellence: 'Jaar van Excellentie'
        },
        expertise: {
           title: 'Onze Expertise',
           subtitle: 'Van intieme theaters tot grote operahuizen, wij creëren de onzichtbare magie die voorstellingen tot leven brengt.',
           stageRigging: {
             title: 'Theatertechnische hijsinstallaties',
             description: 'Wij bouwen van a-z theaterlieren op maat naar uw theater. We hebben oplossingen voor kleine en grote zalen.'
           },
           lightingSystems: {
             title: 'Zelfoptrekkende trussen',
             description: 'Zelfklimmende trussen door middel van 2 motoren in een standaard vierkantstruss. Ideaal voor zalen met weinig fundatiemogelijkheden. De trussen zijn stil doordat het met staalkabels gehesen word en hangen heel stabiel doordat het 4 staalkabels zijn.'
           },
           movableFloors: {
             title: 'Beweegbare Vloeren',
             description: 'Revolutionaire beweegbare vloersystemen die ruimtes onmiddellijk transformeren, meerlagige podia en orkestbakken creëren met architectonische precisie.'
           }
         },
         signatureProjects: {
           title: 'Kenmerkende Projecten',
           musis: {
             title: 'Musis Sacrum, Arnhem',
             year: '2017',
             location: 'Nederland',
             description: 'Een technologisch meesterwerk met zes precisie trommeltrekken, negen onafhankelijk bestuurde beweegbare vloeren en drie orkestliften. Deze installatie vertegenwoordigt het toppunt van moderne theatertechniek.',
             features: {
               trommeltrekken: '6 Trommeltrekken',
               variableSpeed: 'Variabele snelheidsregeling',
               movableFloors: '9 Beweegbare Vloeren',
               independentOperation: 'Onafhankelijke bediening'
             }
           },
           schalm: {
             title: 'De Schalm, Veldhoven',
             year: '2015',
             location: 'Nederland',
             description: 'Een complete renovatie die mogelijkheden uitbreidde met twee nieuwe zijbruggen, een bredere portaalbrug en vier state-of-the-art trommeltrekken systemen, waardoor een traditionele ruimte werd getransformeerd tot een modern wonder.',
             features: {
               renovation: 'Complete Renovatie',
               modernTechnology: 'Moderne technologie integratie',
               trommeltrekken: '4 Trommeltrekken',
               enhanced: 'Verbeterde mogelijkheden'
             }
           }
         },
         philosophy: {
           quote: 'The show must go on!',
           description: 'Kwaliteit en oplossingsgericht denken staan centraal in alles wat we doen. Zo garanderen we perfectie in elk detail. Daarnaast zijn wij ook een betrouwbare toeleverancier van hoogwaardige onderdelen, voor wie dezelfde topkwaliteit zoekt.',
           author: 'Han Stakebrand',
           title: 'Oprichter & Technisch Directeur'
         },
         contactPreview: {
           title: 'Laten We Iets Buitengewoons Creëren',
           description: 'Of u nu een nieuw theater plant, een bestaande ruimte renoveert of op maat gemaakte oplossingen nodig heeft, wij zijn er om uw visie tot leven te brengen met dezelfde toewijding die ons al meer dan 20 jaar drijft.',
           address: 'Rodenrijt 80, 3930 Hamont-Achel, België',
           phone: '+32(0)11-667894',
           email: 'info@stapotech.be'
         },
         cta: {
            title: 'Klaar Om Uw Theater Te Transformeren?',
            subtitle: 'Laten we uw projectvereisten bespreken en samen iets buitengewoons creëren. Neem vandaag nog contact op met ons team van experts.'
          }
        },
        // About Page
        about: {
          title: 'Over Ons',
          subtitle: 'Van onze oprichting in 2001 tot de innovaties van vandaag, Stapotech BV zet de erfenis van excellentie in theatertechnologie en op maat gemaakte oplossingen voort.',
          history: {
            title1: 'Smederij J.H. Stakebrand',
            desc1: 'Op 13 februari 1888 opende Smederij J.H. Stakebrand haar deuren aan de Lijnbaansgracht 290 in Amsterdam. In 1923 volgde een uitbreiding met nog twee aangrenzende panden en werd de naam gewijzigd naar J.H. Stakebrand & Zoon. Op deze historische locatie hebben maar liefst drie generaties van de familie Stakebrand met vakmanschap en toewijding hun ambacht uitgeoefend.',
            title2: '100-jarig bestaan',
            desc2: 'In 2002 vierde ons bedrijf zijn 100-jarig bestaan, destijds onder de naam Stakebrand B.V. Vanuit onze vestiging in Heeze en met een Belgische dochteronderneming, Stakebrand B.V.B.A. in Hamont-Achel, hebben wij een rijke historie opgebouwd. Onder leiding van Han Stakebrand, vierde generatie, en zijn vader Jan Stakebrand, derde generatie, groeide het familiebedrijf verder uit. In deze periode werden belangrijke technologische innovaties doorgevoerd: de ontwikkeling van de traditionele handbediende trekkenwand naar de moderne, elektrisch aangedreven uitvoering.',
            title3: 'Stapotech BV',
            desc3: 'Sinds 2001 werken wij vanuit België onder de naam Stapotech BV (Stakebrand Podium Technieken). In 2024 nam de vijfde generatie, Lisanne Stakebrand, het roer over. Vandaag combineren wij vakmanschap met sterke partnerschappen, zodat wij klantgericht en efficiënt kunnen werken met de kwaliteit die ons al meer dan een eeuw kenmerkt.'
          },
          location: {
            title: 'Onze Locatie',
            headquarters: 'Hoofdkantoor',
            production: 'Productielocatie',
            serviceArea: 'Servicegebied',
            serviceAreaDesc: 'België & Nederland - Slechts 10 minuten van de Nederlandse grens'
          },
          team: {
            title: 'Ons Team',
            subtitle: 'Ontmoet de experts die tientallen jaren ervaring en passie meebrengen naar elk project',
            han: {
              name: 'Han Stakebrand',
              role: 'Oprichter & Technisch Directeur',
              experience: '30+ jaar in theatertechnologie'
            },
            lisanne: {
              name: 'Lisanne Stakebrand',
              role: '5de Generatie - Operationeel Directeur',
              experience: '15+ jaar in theatertechnologie innovatie'
            }
          }
        },
        // Portfolio Page
        portfolio: {
          title: 'Ons Portfolio',
          subtitle: 'Ontdek onze uitzonderlijke theaterinstallaties en technische oplossingen in België en Nederland',
          projects: {
            musis: {
              title: 'Musis Sacrum Arnhem',
              year: '2017',
              description: '6 trommeltrekken, 9 beweegbare vloeren, 3 orkestliften, variabel podium'
            },
            schalm: {
              title: 'De Schalm Veldhoven',
              year: '2015',
              description: 'Complete renovatie met 4 trommeltrekken en verbeterde mogelijkheden'
            },
            castellum: {
              title: 'Theater Castellum',
              year: '2018',
              description: 'Nieuwe theaterbouw met geavanceerde podiumtechnologie en moderne takelsystemen'
            },
            vondel: {
              title: 'Vondelpark Theater',
              year: '2019',
              description: 'Regionaal theater upgrade met verbeterde verlichtingssystemen en beweegbare podiumelementen'
            },
            leest: {
              title: 'Cultureel Centrum De Leest',
              year: '2020',
              description: 'Kleine zaal renovatie met compacte takeloplossingen en flexibele podiumconfiguratie'
            },
            corrosia: {
              title: 'Corrosia Cultureel Centrum',
              year: '2021',
              description: 'Modern cultureel centrum met geïntegreerde theatertechnologie en multifunctionele podiumsystemen'
            }
          }
        },
        // News Page
        news: {
          title: 'Laatste Nieuws',
          subtitle: 'Blijf op de hoogte van onze laatste projecten, innovaties en branche-inzichten',
          newsletter: {
            title: 'Abonneer op Onze Nieuwsbrief',
            subtitle: 'Ontvang de laatste updates van Stapotech in uw inbox',
            emailPlaceholder: 'Voer uw e-mailadres in',
            subscribe: 'Abonneren',
            thankYou: 'Dank Je Wel',
            successMessage: 'U bent succesvol geabonneerd op onze nieuwsbrief.'
          }
        },
        // Metal Designs Page
        metalDesigns: {
          title: 'Metaal Ontwerpen',
          subtitle: 'Op maat gemaakte lasersnij- en poedercoating ontwerpen voor logo\'s, namen en interieurelementen',
          services: {
            title: 'Op Maat Gemaakte Metaaloplossingen',
            precision: {
              title: 'Lasersnij Precisie',
              description: 'Hoge precisie lasersnijden voor materialen tot 2mm dikte'
            },
            coating: {
              title: 'Poedercoating',
              description: 'Duurzame poedercoating in verschillende kleuren en afwerkingen'
            },
            custom: {
              title: 'Op Maat Gemaakte Ontwerpen',
              description: 'Logo\'s, namen, interieurelementen en decoratieve stukken'
            }
          },
          process: {
            title: 'Ons Proces',
            subtitle: 'Van concept tot voltooiing',
            steps: {
              design: {
                title: 'Ontwerp',
                description: 'We werken samen met u om het perfecte ontwerp voor uw behoeften te creëren'
              },
              cutting: {
                title: 'Lasersnijden',
                description: 'Precisie lasersnijden met state-of-the-art apparatuur'
              },
              coating: {
                title: 'Coating',
                description: 'Professionele poedercoating in uw keuze van kleuren'
              },
              delivery: {
                title: 'Levering',
                description: 'Kwaliteitsgecontroleerd en geleverd volgens uw specificaties'
              }
            }
          },
          requestQuote: 'Vraag Offerte Aan',
          cta: {
            title: 'Klaar Om Uw Project Te Starten?',
            description: 'Neem vandaag contact met ons op om uw op maat gemaakte metaalontwerp vereisten te bespreken en een persoonlijke offerte te krijgen.'
          }
        },
        // Careers Page
        careers: {
          title: 'Vacatures',
          subtitle: 'Word lid van ons team van theatertechnologie experts',
          openPositions: 'Open Posities',
          loading: 'Vacatures laden...',
          noVacancies: 'Momenteel geen vacatures beschikbaar.',
          requirements: 'Vereisten',
          location: 'Locatie',
          type: 'Type',
          salary: 'Salaris',
          applyNow: 'Solliciteer Nu',
          submitting: 'Versturen...',
          whyWorkWithUs: 'Waarom Met Ons Werken?',
          whyWorkWithUsSubtitle: 'Word lid van een team dat innovatie, kwaliteit en vakmanschap waardeert',
          benefits: {
            excellence: {
              title: '100+ Jaar Excellentie',
              description: 'Maak deel uit van een bedrijf met meer dan een eeuw expertise en innovatie in theatertechnologie.'
            },
            growth: {
              title: 'Professionele Groei',
              description: 'Ontwikkel uw vaardigheden in geavanceerde theatertechnologie en op maat gemaakte metaalontwerp projecten.'
            },
            team: {
              title: 'Team Omgeving',
              description: 'Werk samen met gepassioneerde professionals die geven om kwaliteit en klanttevredenheid.'
            }
          },
          jobs: {
            installer: {
              title: 'Technisch Installateur',
              description: 'Technisch Installateur gezocht, 40u/week. Ervaring met theatertakelsystemen en mechanische installaties vereist.',
              requirements: 'Vereisten: Technische opleiding, 3+ jaar ervaring, flexibel rooster',
              apply: 'Solliciteer Nu'
            },
            designer: {
              title: 'Ontwerp Specialist',
              description: 'Ontwerp Specialist, creatieve input voor op maat gemaakte metaalontwerpen en theaterinstallaties.',
              requirements: 'Vereisten: Ontwerpopleiding, CAD ervaring, creatieve mindset',
              apply: 'Solliciteer Nu'
            }
          },
          applicationForm: {
            title: 'Sollicitatieformulier',
            name: 'Volledige Naam',
            email: 'E-mailadres',
            phone: 'Telefoonnummer',
            position: 'Functie Waarvoor Gesolliciteerd',
            coverLetter: 'Motivatiebrief',
            cv: 'Upload CV',
            submit: 'Sollicitatie Versturen',
            submitted: 'Sollicitatie Verstuurd',
            successMessage: 'Dank u voor uw interesse. We zullen uw sollicitatie beoordelen en binnenkort contact met u opnemen.'
          }
        },
        // Contact Page
        contact: {
          title: 'Neem Contact Op',
          subtitle: 'Klaar om uw theatervisie tot leven te brengen? Neem contact op met onze experts',
          getInTouch: 'Neem Contact Op',
          headquarters: 'Hoofdkantoor',
          production: 'Productielocatie',
          phoneAndFax: 'Telefoon & Fax',
          email: 'E-mail',
          sendMessage: 'Verstuur een Bericht',
          contactInformation: 'Contactinformatie',
          form: {
            name: 'Naam',
            email: 'E-mail',
            address: 'Adres',
            postcode: 'Postcode',
            city: 'Stad',
            phone: 'Telefoon',
            message: 'Bericht',
            submit: 'Verstuur Bericht',
            thankYou: 'Dank Je Wel',
            successMessage: 'Uw bericht is succesvol verzonden. We nemen binnenkort contact met u op.'
          },
          map: {
            title: 'Vind Ons',
            description: 'Gelegen in Hamont-Achel, België - Slechts 10 minuten van de Nederlandse grens',
            headquarters: 'Hoofdkantoor',
            productionFacility: 'Productiefaciliteit'
          }
        },
        // Translation Demo Page
        translationDemo: {
          title: 'Vertaalsysteem Demo',
          subtitle: 'Test automatische en handmatige vertaalfuncties',
          manualSection: 'Handmatige Vertalingen'
        },
      partners: {
        title: 'Partners',
        description: 'Al geruime tijd zijn wij toeleverancier van hoogwaardige onderdelen. Dankzij ons uitgebreide en moderne machinepark kunnen wij snel en flexibel inspelen op de wensen van onze opdrachtgevers. Onze diepgaande sectorervaring stelt ons in staat om sterke, langdurige samenwerkingsverbanden aan te gaan met diverse partners, waarbij wij gezamenlijk streven naar optimale resultaten.',
      },
      // Footer
      footer: {
        company: 'Stapotech BV',
        description: 'Uw partner in podiumtechniek, waar elk project zijn eigen verhaal vertelt.',
        quickLinks: 'Snelle Links',
        services: 'Diensten',
        contact: 'Contact',
        followUs: 'Volg Ons',
        copyright: '© 2025 Stapotech BV. Alle rechten voorbehouden.',
        rights: 'Alle rechten voorbehouden.',
        privacy: 'Privacybeleid',
        terms: 'Algemene Voorwaarden',
        links: {
          home: 'Home',
          about: 'Over Ons',
          portfolio: 'Portfolio',
          news: 'Nieuws',
          metalDesigns: 'Metaal Ontwerpen',
          careers: 'Vacatures',
          contact: 'Contact'
        },
        servicesList: {
          hijsinstallaties: 'Theaterhijsinstallaties',
          vloeren: 'Beweegbare vloeren',
          draaitonelen: 'Draaitonelen',
          trussen: 'Zelfoptrekkende trussen',
          metal: 'Metaal Ontwerpen',
          theaterTechnology: 'Theatertechnologie',
          customMetalDesigns: 'Op Maat Gemaakte Metaalontwerpen',
          installation: 'Installatie & Onderhoud',
          consultation: 'Consultatie & Ontwerp'
        },
        companyList: {
          about: 'Over Ons',
          portfolio: 'Portfolio',
          news: 'Nieuws',
          contact: 'Contact'
        },
        contactInfo: {
          address: 'Adres',
          phone: 'Telefoon',
          email: 'E-mail',
          hours: 'Openingstijden',
          weekdays: 'Ma-Vr: 8:00 - 17:00',
          weekend: 'Za-Zo: Gesloten'
        }
      }
    }
  }
};

// Enhanced i18n with intelligent automatic translation
i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('preferred-language') || 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    // Handle missing translations with intelligent routing
    missingKeyHandler: (lng, ns, key, fallbackValue) => {
      // Skip auto-translation for manual translation keys (nav, footer, etc.)
      const manualKeys = ['nav.', 'footer.', 'common.readMore', 'common.learnMore', 'common.getStarted'];
      if (manualKeys.some(pattern => key.startsWith(pattern))) {
        console.log(`⚠️ Manual translation missing for key: ${key} in language: ${lng}`);
        return fallbackValue || key;
      }

      // Only auto-translate for supported languages and when fallback exists
      const supportedLanguages = ['en', 'nl'];
      if (lng !== 'en' && fallbackValue && supportedLanguages.includes(lng)) {
        // Queue translation for later to avoid blocking UI
        setTimeout(async () => {
          try {
            const response = await fetch('/api/translation/translate', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                text: fallbackValue,
                from: 'en',
                to: lng
              })
            });

            if (response.ok) {
              const data = await response.json();
              const translated = data.translatedText;
              
              if (translated && translated !== fallbackValue) {
                i18n.addResource(lng, ns, key, translated);
                // Force re-render by emitting languageChanged event
                i18n.emit('languageChanged', lng);
                console.log(`🔄 Auto-translated: ${key} → ${translated}`);
              }
            }
          } catch (error) {
            console.warn(`Auto-translation failed for key: ${key}`, error);
          }
        }, 0);
      }
      
      return fallbackValue || key;
    },
    parseMissingKeyHandler: (key) => {
      return key;
    }
  });

// Preload common translations when language changes
i18n.on('languageChanged', async (lng) => {
  const supportedLanguages = ['en', 'nl'];
  if (lng !== 'en' && supportedLanguages.includes(lng)) {
    // Preload common translations for better UX
    try {
      const commonPhrases = [
        'Loading...', 'Please wait...', 'Error occurred', 'Try again',
        'Success', 'Failed', 'Save', 'Cancel', 'Submit', 'Close',
        'Back', 'Next', 'Previous', 'Home', 'About', 'Contact',
        'Services', 'Portfolio', 'News', 'Welcome', 'Learn More'
      ];

      const response = await fetch('/api/translation/translate-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          texts: commonPhrases,
          from: 'en',
          to: lng
        })
      });

      if (response.ok) {
        const data = await response.json();
        const translatedTexts = data.translatedTexts;
        
        // Cache the translations
        commonPhrases.forEach((phrase, index) => {
          if (translatedTexts[index]) {
            i18n.addResource(lng, 'translation', `common.${phrase.toLowerCase().replace(/[^a-z0-9]/g, '')}`, translatedTexts[index]);
          }
        });
        
        console.log(`✅ Preloaded ${translatedTexts.length} common translations for ${lng}`);
      }
    } catch (error) {
      console.warn('Failed to preload common translations:', error);
    }
  }
});

// Add method to manually add translations for new languages
i18n.addLanguageSupport = (languageCode, languageName) => {
  const supportedLanguages = ['en', 'nl'];
  if (!supportedLanguages.includes(languageCode)) {
    supportedLanguages.push(languageCode);
  }
  
  // Initialize empty resources for the new language
  if (!i18n.hasResourceBundle(languageCode, 'translation')) {
    i18n.addResourceBundle(languageCode, 'translation', {}, true, true);
  }
  
  console.log(`✅ Added support for language: ${languageName} (${languageCode})`);
};

// Add method to translate dynamic content from dashboard/API
i18n.translateDynamicContent = async (content, targetLang = null) => {
  const currentLang = targetLang || i18n.language;
  if (currentLang === 'en') return content;
  
  try {
    const response = await fetch('/api/translation/translate-object', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content: content,
        from: 'en',
        to: currentLang
      })
    });

    if (response.ok) {
      const data = await response.json();
      return data.translatedContent || content;
    }
    
    return content;
  } catch (error) {
    console.error('Failed to translate dynamic content:', error);
    return content;
  }
};

export default i18n;